export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    session: () => [...queryKeys.auth.all, 'session'] as const,
  },

  // Organisations
  organisations: {
    all: ['organisations'] as const,
    lists: () => [...queryKeys.organisations.all, 'list'] as const,
    list: <T extends object>(filters: T) =>
      [...queryKeys.organisations.lists(), filters] as const,
    details: () => [...queryKeys.organisations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.organisations.details(), id] as const,
    employees: (orgId: string) => [...queryKeys.organisations.detail(orgId), 'employees'] as const,
    pendingEmployees: (orgId: string) => [...queryKeys.organisations.detail(orgId), 'pendingEmployees'] as const,
    brands: (orgId: string) => [...queryKeys.organisations.detail(orgId), 'brands'] as const,
    modules: (orgId: string) => [...queryKeys.organisations.detail(orgId), 'modules'] as const,
    config: (orgId: string) => [...queryKeys.organisations.detail(orgId), 'config'] as const,
    aliases: (orgId: string) => [...queryKeys.organisations.detail(orgId), 'aliases'] as const,
    files: (orgId: string) => [...queryKeys.organisations.detail(orgId), 'files'] as const,
    // Links
    links: (orgId: string, type: 'licensor' | 'licensee') =>
      [...queryKeys.organisations.detail(orgId), 'links', type] as const,
    linkedBrands: (orgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'linkedBrands'] as const,
    linkedLicensees: (orgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'linkedLicensees'] as const,
    agentLinks: (orgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'agentLinks'] as const,
    // Tags
    tags: (orgId: string) => [...queryKeys.organisations.detail(orgId), 'tags'] as const,
    tagAssignments: (orgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'tagAssignments'] as const,
    tagValues: (orgId: string, tagId: string) =>
      [...queryKeys.organisations.detail(orgId), 'tagValues', tagId] as const,
    tagsForLinking: (orgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'tagsForLinking'] as const,
    licensorTagLinks: (orgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'licensorTagLinks'] as const,
    // IP Hierarchy
    ipLevels: (orgId: string, licensorOrgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'ipLevels', licensorOrgId] as const,
    ipList: (orgId: string, licensorOrgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'ipList', licensorOrgId] as const,
    // Product Categories
    categories: (orgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'categories'] as const,
    categoryLevels: (orgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'categoryLevels'] as const,
    // Export/Update
    exportHistory: (orgId: string, licensorOrgId?: string) =>
      [...queryKeys.organisations.detail(orgId), 'exportHistory', licensorOrgId] as const,
    importHistory: (orgId: string, licensorOrgId?: string) =>
      [...queryKeys.organisations.detail(orgId), 'importHistory', licensorOrgId] as const,
    // Retailer Listings
    retailerListings: (orgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'retailerListings'] as const,
    retailerListingExports: (orgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'retailerListingExports'] as const,
    retailerListingIngests: (orgId: string, jobType: 'upsert' | 'delete') =>
      [...queryKeys.organisations.detail(orgId), 'retailerListingIngests', jobType] as const,
    // Concepts & Contracts Upload
    conceptsHistory: (orgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'conceptsHistory'] as const,
    contractsHistory: (orgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'contractsHistory'] as const,
    // Validations
    validations: (orgId: string) =>
      [...queryKeys.organisations.detail(orgId), 'validations'] as const,
  },

  // Validations (cross-org)
  validations: {
    all: ['validations'] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: <T extends object>(filters: T) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    logins: (userId: string) => [...queryKeys.users.detail(userId), 'logins'] as const,
    linkedOrgs: (userId: string) =>
      [...queryKeys.users.detail(userId), 'linkedOrganisations'] as const,
  },

  // Common Files
  commonFiles: {
    all: ['commonFiles'] as const,
    list: () => [...queryKeys.commonFiles.all, 'list'] as const,
  },

  // Global search lightweight indexes
  search: {
    all: ['search-index'] as const,
    organisations: () => [...queryKeys.search.all, 'organisations'] as const,
    users: () => [...queryKeys.search.all, 'users'] as const,
  },
} as const;
